'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Component } from 'react';
import { debounce } from 'underscore';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { ContentTypes } from 'academy-shared-js/constants/ContentConstants';
import ApiError from '../../components/Errors/ApiError';
import ContentControlWrapper from '../../components/ContentControlWrapper';
import UsageTracker from '../../utils/UsageTracker';
const BASE_CLASS_NAME = 'chapter-content';

function isInViewport(element) {
  // this method only takes into account the vertical scroll position of the element,
  // not the horizontal one, which is fine for this use case
  const rect = element.getBoundingClientRect();
  const html = document.documentElement;
  return rect.top >= 0 && rect.top <= (window.innerHeight || html.clientHeight);
}

class ChapterContent extends Component {
  constructor(props) {
    super(props);
    this.checkProgress = debounce(this.checkProgress.bind(this), 500);
    this.handleClickNext = this.handleClickNext.bind(this);
    this.maybeMarkCompleted = this.maybeMarkCompleted.bind(this);
    this.handleClickOnChapterContent = this.handleClickOnChapterContent.bind(this);
    this.maybeTrackLink = this.maybeTrackLink.bind(this);
  }

  componentDidMount() {
    this.maybeRegisterProgressHandler();
  }

  componentDidUpdate() {
    this.maybeRegisterProgressHandler();
  }

  componentWillUnmount() {
    this.cancelProgressHandler();
  }

  maybeRegisterProgressHandler() {
    const {
      completed,
      contents
    } = this.props;

    if (!completed && contents) {
      document.addEventListener('scroll', this.checkProgress, true);
      this.checkProgress(); // in case the page does not have a scrollbar at all
    }
  }

  cancelProgressHandler() {
    document.removeEventListener('scroll', this.checkProgress, true);
  }

  checkProgress() {
    if (this.footerRef && isInViewport(this.footerRef)) {
      this.maybeMarkCompleted();
    }
  }

  handleClickNext() {
    this.maybeMarkCompleted();
    this.props.advanceToNextContent();
  }

  maybeMarkCompleted() {
    const {
      contents,
      completed
    } = this.props;

    if (!completed && contents) {
      this.cancelProgressHandler();
      this.props.onComplete();
    }
  }

  handleClickOnChapterContent(event) {
    const {
      target
    } = Object.assign({}, event);
    if (target) this.maybeTrackLink(target.href, target.target);
  }

  maybeTrackLink(href, target) {
    const {
      contentId,
      contentLanguage,
      trackId,
      lessonId
    } = this.props;
    if (!href) return;
    if (!contentId || !trackId && !lessonId) return;
    const eventProps = {
      action: 'open-chapter-link',
      contentId,
      contentLanguage,
      contentType: ContentTypes.CHAPTER,
      trackId,
      lessonId,
      resourceTitle: target.textContent,
      resourceUrl: target.href
    };

    if (target) {
      UsageTracker.track('contentInteraction', eventProps);
    } else {
      UsageTracker.trackExternalLink('contentInteraction', eventProps);
    }
  }

  renderContent() {
    const {
      contents,
      error,
      isLoading
    } = this.props;

    if (isLoading) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true
      });
    }

    if (error) {
      return /*#__PURE__*/_jsx(ApiError, {
        error: error
      });
    }

    return /*#__PURE__*/_jsx("div", {
      className: `${BASE_CLASS_NAME}__content`,
      dangerouslySetInnerHTML: {
        __html: contents
      },
      "data-test-id": "chapter-html-content",
      onClick: this.handleClickOnChapterContent
    });
  }

  render() {
    const {
      name
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": BASE_CLASS_NAME,
      children: [/*#__PURE__*/_jsxs("div", {
        className: BASE_CLASS_NAME,
        children: [/*#__PURE__*/_jsx("h2", {
          className: `${BASE_CLASS_NAME}__title`,
          children: name
        }), this.renderContent()]
      }), /*#__PURE__*/_jsx(ContentControlWrapper, {
        className: `${BASE_CLASS_NAME}__content-control`,
        children: /*#__PURE__*/_jsx("div", {
          ref: div => {
            this.footerRef = div;
          },
          children: /*#__PURE__*/_jsx(UIButton, {
            onClick: this.handleClickNext,
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.common.next"
            })
          })
        })
      })]
    });
  }

}

ChapterContent.propTypes = {
  advanceToNextContent: PropTypes.func.isRequired,
  completed: PropTypes.bool,
  contentId: PropTypes.string,
  contentLanguage: PropTypes.string,
  contents: PropTypes.string,
  error: PropTypes.shape({
    status: PropTypes.number
  }),
  isLoading: PropTypes.bool,
  lessonId: PropTypes.string,
  name: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  trackId: PropTypes.string
};
export default ChapterContent;